/**
 * Fantasy Console 0 - Edit Username Modal
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useAuth, isValidUsername } from '../../context/AuthContext'
import { getAuthErrorMessage } from '../../utils/authErrors'
import styles from './AuthModal.module.css'

interface EditUsernameModalProps {
  isOpen: boolean
  onClose: () => void
}

export function EditUsernameModal({ isOpen, onClose }: EditUsernameModalProps) {
  const { username: currentUsername, updateUsername } = useAuth()
  const [newUsername, setNewUsername] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setNewUsername(currentUsername || '')
      setError(null)
      setTimeout(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      }, 100)
    }
  }, [isOpen, currentUsername])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)

      const trimmed = newUsername.trim()

      if (!isValidUsername(trimmed)) {
        setError(
          'Username must be 3-30 characters and only contain letters, numbers, hyphens, and underscores',
        )
        return
      }

      if (trimmed === currentUsername) {
        onClose()
        return
      }

      setIsLoading(true)

      try {
        await updateUsername(trimmed)
        onClose()
      } catch (err) {
        setError(getAuthErrorMessage(err))
      } finally {
        setIsLoading(false)
      }
    },
    [newUsername, currentUsername, updateUsername, onClose],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  return (
    <div className='modal-overlay' onClick={onClose} onKeyDown={handleKeyDown}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2 className='modal-title'>Change Username</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='modal-body'>
            {error && <div className={styles.error}>{error}</div>}

            <div className='form-group'>
              <label className='form-label' htmlFor='edit-username'>
                New Username
              </label>
              <input
                ref={inputRef}
                id='edit-username'
                type='text'
                className='form-input'
                value={newUsername}
                onChange={(e) => {
                  setNewUsername(e.target.value)
                  setError(null)
                }}
                placeholder='cooldev42'
                maxLength={30}
                required
              />
              <span className={styles.hint}>
                3-30 characters, letters, numbers, hyphens, underscores only
              </span>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-ghost' onClick={onClose}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Username'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
